import {
  CSSProperties,
  RefObject,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export const DROPDOWN_OPEN_STATE_EVENT = "share-dropdown-open-state";

type DropdownListener = (activeId: string | null) => void;

const dropdownListeners = new Set<DropdownListener>();
let activeDropdownId: string | null = null;

function notifyDropdownListeners(id: string | null) {
  activeDropdownId = id;
  dropdownListeners.forEach((listener) => listener(id));
}

function notifyAutoplayState(open: boolean) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(DROPDOWN_OPEN_STATE_EVENT, { detail: { open } }));
  }
}

const dropdownManager = {
  open(id: string) {
    const wasOpen = activeDropdownId !== null;
    notifyDropdownListeners(id);
    if (!wasOpen) notifyAutoplayState(true);
  },
  close(id: string) {
    if (activeDropdownId !== id) return;
    notifyDropdownListeners(null);
    notifyAutoplayState(false);
  },
  closeIfActive(id: string) {
    if (activeDropdownId === id) {
      this.close(id);
    }
  },
  isActive(id: string) {
    return activeDropdownId === id;
  },
};

export function useExclusiveDropdown() {
  const instanceId = useId();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const listener = (activeId: string | null) => {
      setIsOpen(activeId === instanceId);
    };

    dropdownListeners.add(listener);
    listener(activeDropdownId);

    return () => {
      dropdownListeners.delete(listener);
      dropdownManager.closeIfActive(instanceId);
    };
  }, [instanceId]);

  const closeDropdown = useCallback(() => {
    dropdownManager.close(instanceId);
  }, [instanceId]);

  const toggleDropdown = useCallback(() => {
    if (dropdownManager.isActive(instanceId)) {
      dropdownManager.close(instanceId);
    } else {
      dropdownManager.open(instanceId);
    }
  }, [instanceId]);

  return { isOpen, toggleDropdown, closeDropdown };
}

export function usePauseAutoplayOnDropdown(swiperRef: RefObject<{ autoplay?: { stop: () => void; start: () => void } } | null>) {
  useEffect(() => {
    const handleState = (e: Event) => {
      const open = (e as CustomEvent<{ open: boolean }>).detail?.open;
      const swiper = swiperRef.current;
      if (!swiper?.autoplay) return;
      if (open) swiper.autoplay.stop();
      else swiper.autoplay.start();
    };

    window.addEventListener(DROPDOWN_OPEN_STATE_EVENT, handleState);
    return () => window.removeEventListener(DROPDOWN_OPEN_STATE_EVENT, handleState);
  }, [swiperRef]);
}

export const useHandleClickOutside = (ref: any, callback: () => void) => {
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [callback, ref]);
};

type UsePortalDropdownOptions = {
  isOpen: boolean;
  usePortal: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  dropdownPosition?: "up" | "down";
  estimatedHeight?: number;
  onClose: () => void;
};

function computePortalStyle(
  trigger: HTMLElement,
  dropdownPosition: "up" | "down",
  estimatedHeight: number,
): CSSProperties | null {
  const rect = trigger.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;

  const minWidth = 180;
  const openUp = dropdownPosition === "up";
  const left = Math.max(8, Math.min(rect.right - minWidth, window.innerWidth - minWidth - 8));

  return {
    position: "fixed",
    left,
    minWidth,
    zIndex: 9999,
    ...(openUp
      ? { bottom: window.innerHeight - rect.top + 4 }
      : {
          top: rect.bottom + 4,
          maxHeight: "min(400px, calc(100vh - 8px))",
          overflowY: "auto" as const,
        }),
  };
}

function stylesEqual(a: CSSProperties, b: CSSProperties) {
  return (
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.minWidth === b.minWidth &&
    a.maxHeight === b.maxHeight
  );
}

export function usePortalDropdown({
  isOpen,
  usePortal,
  triggerRef,
  dropdownPosition = "down",
  estimatedHeight = 320,
  onClose,
}: UsePortalDropdownOptions) {
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const [isPositionReady, setIsPositionReady] = useState(false);
  const styleRef = useRef<CSSProperties>({});

  const updateDropdownPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!usePortal || !trigger) return false;

    const next = computePortalStyle(trigger, dropdownPosition, estimatedHeight);
    if (!next) return false;

    if (!stylesEqual(styleRef.current, next)) {
      styleRef.current = next;
      setDropdownStyle(next);
    }
    return true;
  }, [usePortal, triggerRef, dropdownPosition, estimatedHeight]);

  useLayoutEffect(() => {
    if (!isOpen || !usePortal) {
      setIsPositionReady(false);
      styleRef.current = {};
      setDropdownStyle({});
      return;
    }

    const positioned = updateDropdownPosition();
    setIsPositionReady(positioned);
  }, [isOpen, usePortal, updateDropdownPosition, triggerRef]);

  useEffect(() => {
    if (!isOpen || !usePortal) return;

    const scrollPositions = new Map<EventTarget, { top: number; left: number }>();

    const getScrollOffsets = (
      target: EventTarget,
    ): { top: number | null; left: number | null } => {
      if (target === document || target === document.documentElement) {
        return { top: window.scrollY, left: window.scrollX };
      }
      if (!(target instanceof HTMLElement)) {
        return { top: null, left: null };
      }

      const top =
        target.scrollHeight > target.clientHeight ? target.scrollTop : null;
      const left =
        target.scrollWidth > target.clientWidth ? target.scrollLeft : null;

      return { top, left };
    };

    let frameId = 0;
    const trackPosition = () => {
      updateDropdownPosition();
      frameId = requestAnimationFrame(trackPosition);
    };
    frameId = requestAnimationFrame(trackPosition);

    const handleScroll = (e: Event) => {
      const target = e.target;
      if (!target) return;

      const { top: scrollTop, left: scrollLeft } = getScrollOffsets(target);
      if (scrollTop === null && scrollLeft === null) return;

      const prev = scrollPositions.get(target) ?? {
        top: scrollTop ?? 0,
        left: scrollLeft ?? 0,
      };

      if (scrollTop !== null) {
        if (scrollTop > prev.top + 1) {
          onClose();
          return;
        }
        prev.top = scrollTop;
      }

      if (scrollLeft !== null && Math.abs(scrollLeft - prev.left) > 1) {
        onClose();
        return;
      }

      if (scrollLeft !== null) {
        prev.left = scrollLeft;
      }

      scrollPositions.set(target, prev);
    };

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY > 0 || Math.abs(e.deltaX) > 0) {
        onClose();
      }
    };

    const handleResize = () => updateDropdownPosition();

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("wheel", handleWheel, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, usePortal, updateDropdownPosition, onClose]);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    return () => onCloseRef.current();
  }, []);

  return { dropdownStyle, isPositionReady };
}
